// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { 
  ProductType, 
  RewardType, 
  ProductReward, 
  ShopProduct, 
  ShopConfiguration,
  validateProduct,
  validateShopConfiguration
} from '../types/shopConfig';

export class ProductRewardClass implements ProductReward {
  type: RewardType;
  amount: number;

  constructor(type: RewardType = RewardType.Coin, amount: number = 0) {
    this.type = type;
    this.amount = amount;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): ProductRewardClass {
    return new ProductRewardClass(obj.type || RewardType.Coin, obj.amount || 0);
  }

  /**
   * Convert to plain object
   */
  toObject(): ProductReward {
    return {
      type: this.type,
      amount: this.amount
    };
  }

  /**
   * Create a copy of this reward
   */
  clone(): ProductRewardClass {
    return new ProductRewardClass(this.type, this.amount);
  }

  /**
   * Check if this reward is valid
   */
  isValid(): boolean {
    return this.amount > 0 && Object.values(RewardType).includes(this.type);
  }
}

export class ShopProductClass implements ShopProduct {
  productID: string;
  type: ProductType;
  iosID: string;
  googleID: string;
  enabled: boolean;
  priority: number;
  rewards: ProductRewardClass[];
  json_config: Record<string, any> | null;

  constructor(
    productID: string = '',
    type: ProductType = ProductType.Consumable,
    iosID: string = '',
    googleID: string = '',
    enabled: boolean = true,
    priority: number = 0,
    rewards?: ProductRewardClass[],
    json_config?: Record<string, any> | null
  ) {
    this.productID = productID;
    this.type = type;
    this.iosID = iosID;
    this.googleID = googleID;
    this.enabled = enabled;
    this.priority = priority;
    this.rewards = rewards || [new ProductRewardClass(RewardType.Coin, 100)];
    this.json_config = json_config || null;
  }

  /**
   * Create from plain object
   */
  static fromObject(obj: any): ShopProductClass {
    const product = new ShopProductClass();
    product.productID = obj.productID || '';
    product.type = obj.type || ProductType.Consumable;
    product.iosID = obj.iosID || '';
    product.googleID = obj.googleID || '';
    product.enabled = obj.enabled !== undefined ? obj.enabled : true;
    product.priority = obj.priority || 0;
    
    // Convert rewards
    product.rewards = [];
    if (obj.rewards && Array.isArray(obj.rewards)) {
      product.rewards = obj.rewards.map((reward: any) => ProductRewardClass.fromObject(reward));
    }
    
    product.json_config = obj.json_config || null;
    return product;
  }

  /**
   * Convert to plain object
   */
  toObject(): ShopProduct {
    return {
      productID: this.productID,
      type: this.type,
      iosID: this.iosID,
      googleID: this.googleID,
      enabled: this.enabled,
      priority: this.priority,
      rewards: this.rewards.map(reward => reward.toObject()),
      json_config: this.json_config
    };
  }

  /**
   * Create a copy of this product
   */
  clone(): ShopProductClass {
    return new ShopProductClass(
      this.productID,
      this.type,
      this.iosID,
      this.googleID,
      this.enabled,
      this.priority,
      this.rewards.map(reward => reward.clone()),
      this.json_config ? { ...this.json_config } : null
    );
  }

  /**
   * Validate product configuration
   */
  validate(): string[] {
    return validateProduct(this.toObject());
  }

  /**
   * Check if product is valid
   */
  isValid(): boolean {
    return this.validate().length === 0;
  }

  /**
   * Update a field in the product
   */
  updateField(field: string, value: any): void {
    if (field.includes('.')) {
      // Handle nested fields like 'rewards.0.amount'
      const parts = field.split('.');
      let current: any = this;
      
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    } else {
      (this as any)[field] = value;
    }
  }

  /**
   * Add a new reward
   */
  addReward(reward: ProductRewardClass): void {
    this.rewards.push(reward);
  }

  /**
   * Remove a reward by index
   */
  removeReward(index: number): void {
    if (index >= 0 && index < this.rewards.length) {
      this.rewards.splice(index, 1);
    }
  }

  /**
   * Get total coin value from all rewards
   */
  getTotalCoinValue(): number {
    return this.rewards
      .filter(reward => reward.type === RewardType.Coin)
      .reduce((total, reward) => total + reward.amount, 0);
  }
}

export class ShopSettingsClass implements ShopConfiguration {
  products: Record<string, ShopProductClass>;

  constructor(products?: Record<string, ShopProductClass>) {
    this.products = products || this.getDefaultProducts();
  }

  /**
   * Get default product configuration
   */
  private getDefaultProducts(): Record<string, ShopProductClass> {
    return {
      "coins_100": new ShopProductClass(
        "coins_100",
        ProductType.Consumable,
        "com.investaplay.investaplay.coins_100",
        "com.investaplay.games.coins1",
        true,
        0,
        [new ProductRewardClass(RewardType.Coin, 100)]
      ),
      "coins_200": new ShopProductClass(
        "coins_200",
        ProductType.Consumable,
        "com.investaplay.investaplay.coins_500",
        "com.investaplay.games.coins2",
        true,
        1,
        [new ProductRewardClass(RewardType.Coin, 200)]
      ),
      "coins_300": new ShopProductClass(
        "coins_300",
        ProductType.Consumable,
        "com.investaplay.investaplay.coins_1000",
        "com.investaplay.games.coins3",
        true,
        2,
        [new ProductRewardClass(RewardType.Coin, 300)]
      )
    };
  }

  /**
   * Serialize to JSON string with validation
   */
  toJSON(): string {
    try {
      // Validate before serialization
      const validationErrors = this.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings during serialization:', validationErrors);
      }
      
      // Use manual serialization (more reliable)
      return JSON.stringify(this.toObject(), null, 2);
    } catch (error) {
      console.error('Serialization failed:', error);
      // Fallback to basic JSON.stringify
      return JSON.stringify({
        products: this.products
      }, null, 2);
    }
  }

  /**
   * Deserialize from JSON string with validation
   */
  static fromJSON(jsonString: string): ShopSettingsClass {
    try {
      const plainObject = JSON.parse(jsonString);
      return ShopSettingsClass.fromObject(plainObject);
    } catch (error) {
      console.error('JSON deserialization failed:', error);
      return new ShopSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Create from plain object with validation
   */
  static fromObject(obj: any): ShopSettingsClass {
    try {
      const instance = new ShopSettingsClass();
      
      if (obj.products) {
        instance.products = {};
        Object.entries(obj.products).forEach(([key, productObj]: [string, any]) => {
          instance.products[key] = ShopProductClass.fromObject(productObj);
        });
      }
      
      // Validate created instance
      const validationErrors = instance.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings after object conversion:', validationErrors);
      }
      
      return instance;
    } catch (error) {
      console.error('Object conversion failed:', error);
      return new ShopSettingsClass(); // Return default settings on error
    }
  }

  /**
   * Convert to plain object with validation
   */
  toObject(): any {
    try {
      // Validate before conversion
      const validationErrors = this.validate();
      if (validationErrors.length > 0) {
        console.warn('Validation warnings during object conversion:', validationErrors);
      }
      
      // Manual conversion (more reliable)
      const products: Record<string, any> = {};
      Object.entries(this.products).forEach(([key, product]) => {
        products[key] = product.toObject();
      });
      
      return {
        products
      };
    } catch (error) {
      console.error('Object conversion failed:', error);
      // Fallback to basic conversion
      return {
        products: this.products
      };
    }
  }

  /**
   * Create a deep copy of the settings
   */
  clone(): ShopSettingsClass {
    const clonedProducts: Record<string, ShopProductClass> = {};
    Object.entries(this.products).forEach(([key, product]) => {
      clonedProducts[key] = product.clone();
    });
    return new ShopSettingsClass(clonedProducts);
  }

  /**
   * Validate all product configurations
   */
  validate(): string[] {
    return validateShopConfiguration(this.toObject());
  }

  /**
   * Check if all settings are valid
   */
  isValid(): boolean {
    return this.validate().length === 0;
  }

  /**
   * Reset to default configuration
   */
  resetToDefaults(): void {
    this.products = this.getDefaultProducts();
  }

  /**
   * Update a product field
   */
  updateProductField(productKey: string, field: string, value: any): void {
    if (this.products[productKey]) {
      this.products[productKey].updateField(field, value);
    }
  }

  /**
   * Add a new product
   */
  addProduct(key: string, product: ShopProductClass): void {
    this.products[key] = product;
  }

  /**
   * Remove a product
   */
  removeProduct(key: string): void {
    delete this.products[key];
  }

  /**
   * Get product by key
   */
  getProduct(key: string): ShopProductClass | undefined {
    return this.products[key];
  }

  /**
   * Get all product keys
   */
  getProductKeys(): string[] {
    return Object.keys(this.products);
  }

  /**
   * Get products sorted by priority
   */
  getProductsSortedByPriority(): Array<{ key: string; product: ShopProductClass }> {
    return Object.entries(this.products)
      .map(([key, product]) => ({ key, product }))
      .sort((a, b) => a.product.priority - b.product.priority);
  }

  /**
   * Get enabled products only
   */
  getEnabledProducts(): Record<string, ShopProductClass> {
    const enabledProducts: Record<string, ShopProductClass> = {};
    Object.entries(this.products).forEach(([key, product]) => {
      if (product.enabled) {
        enabledProducts[key] = product;
      }
    });
    return enabledProducts;
  }

  /**
   * Check if settings have changed compared to another instance
   */
  hasChangedFrom(other: ShopSettingsClass): boolean {
    return JSON.stringify(this.toObject()) !== JSON.stringify(other.toObject());
  }

  /**
   * Update from another ShopSettingsClass instance
   */
  updateFrom(other: ShopSettingsClass): void {
    this.products = other.clone().products;
  }

  /**
   * Get statistics about the configuration
   */
  getStats(): {
    totalProducts: number;
    enabledProducts: number;
    disabledProducts: number;
    consumableProducts: number;
    nonConsumableProducts: number;
    subscriptionProducts: number;
    totalRewards: number;
    averageCoinValue: number;
  } {
    const products = Object.values(this.products);
    const enabledProducts = products.filter(p => p.enabled);
    const totalCoinValue = products.reduce((total, product) => total + product.getTotalCoinValue(), 0);
    
    return {
      totalProducts: products.length,
      enabledProducts: enabledProducts.length,
      disabledProducts: products.length - enabledProducts.length,
      consumableProducts: products.filter(p => p.type === ProductType.Consumable).length,
      nonConsumableProducts: products.filter(p => p.type === ProductType.NonConsumable).length,
      subscriptionProducts: products.filter(p => p.type === ProductType.Subscription).length,
      totalRewards: products.reduce((total, product) => total + product.rewards.length, 0),
      averageCoinValue: products.length > 0 ? totalCoinValue / products.length : 0
    };
  }
}
